import React, { useMemo } from 'react';

import { ItemValidator } from '../../../../../services/ItemValidator';
import { DeselectedMenuItemAction } from '../../../../actions/menuActions';
import { useStudentDispatch } from '../../../../hooks/useStudentDispatch';
import { useSelectItem } from '../../../../store/reducers/items/useSelectItem';
import { addStartOfItem } from '../../../../store/reducers/student/tracking/itemsTrackingActions';
import { noTrackedItemsSelector } from '../../../../store/reducers/student/tracking/itemsTrackingSelectors';
import { getCurrentItem } from '../../../../store/selectors/getCurrentItem';
import { useStudentSelector } from '../../../../store/selectors/useStudentSelector';
import { ItemDetailsType, MenuItem, MenuItemProps } from '../../components/student/MenuItem';
import { Quiz, TrackingStatus } from '@ael/core-api';
import { createPassedFailedProgressDisplayConfiguration } from '@ael/shared-components-library';

interface OwnProps {
  item: Quiz;
  trackingStatus?: TrackingStatus;
  children: React.ReactNode;
  parentItemId: string | null;
  nestedLevel: number;
  isLinearModeOn: boolean;
}

export const QuizItem: React.FC<OwnProps> = (ownProps) => {
  const dispatch = useStudentDispatch();
  const { goToItem } = useSelectItem();
  const currentItem = useStudentSelector(getCurrentItem);
  const noTrackedItems = useStudentSelector(noTrackedItemsSelector);
  const isItemMandatoryOrCompletionDisabled = noTrackedItems || ownProps.item.isMandatory;
  const isValid = ItemValidator.isValid(ownProps.item);
  const stateProps = {
    isSelected: currentItem === ownProps.item,
    trackingStatus: ownProps.trackingStatus,
    disabled: !isValid,
    progress:
      isValid && ownProps.trackingStatus
        ? createPassedFailedProgressDisplayConfiguration(ownProps.trackingStatus)
        : undefined,
    showItemCompletion: isItemMandatoryOrCompletionDisabled,
  };
  const dispatchProps = useMemo(
    () => ({
      onClick: () => {
        goToItem(ownProps.item.id);
        dispatch(addStartOfItem(ownProps.item.id));
      },
      onDeselectMenuItem: () => {
        dispatch(DeselectedMenuItemAction(ownProps.item));
      },
    }),
    [dispatch, goToItem, ownProps.item]
  );

  const mergeProps: MenuItemProps = {
    children: ownProps.children,
    menuItemDetails: {
      itemDetailsType: ItemDetailsType.Item,
      active: stateProps.isSelected,
      trackingStatus: stateProps.trackingStatus,
      disabled: stateProps.disabled,
      parentItemId: ownProps.parentItemId,
      progress: stateProps.progress,
      showItemCompletion: stateProps.showItemCompletion,
      nestedLevel: ownProps.nestedLevel,
      hideChevron: true,
      itemId: ownProps.item.id,
    },
    isLinearModeOn: ownProps.isLinearModeOn,
    onClick: stateProps.isSelected ? dispatchProps.onDeselectMenuItem : dispatchProps.onClick,
  };

  return <MenuItem {...mergeProps} />;
};
